import { gelNumberOf } from '@/utils/money';
import type {
    AssetCategory,
    AssetEntryLink,
    AssetEntryLinkKind,
    CommitDepreciationRunResponse,
    CreateFixedAssetResponse,
    DepreciationMethod,
    DepreciationRun,
    DepreciationRunLine,
    DepreciationRunLineReason,
    DepreciationScheduleRow,
    DisposalPreview,
    DraftEntryPreview,
    FixedAsset,
    FixedAssetReconciliation,
    FixedAssetReconciliationLine,
    FixedAssetRegisterSummary,
    FixedAssetStatus,
    GetAssetCategoriesResponse,
    GetFixedAssetResponse,
    GetFixedAssetsResponse,
    JournalLine,
    LocaleCode,
    PlausibilityWarning,
    CommitDisposalResponse,
    PreviewDepreciationRunResponse,
    PreviewDisposalResponse,
    UpdateFixedAssetResponse,
} from '@/api/models';
import {
    decimalGelOf,
    mapJournalEntry,
    mapValidationState,
} from '../journal/journalMapping';

/**
 * Wire ⇄ domain mapping for the Fixed Assets register (bos-api).
 *
 * **This is the only file that knows the server's field names.** Everything
 * downstream — the service, the query hooks, the page hook, the table, the
 * drawer, the run dialog, the dispose dialog — sees the domain shapes in
 * `@/api/models/FixedAssets`. If the wire disagrees with what is assumed here,
 * this file is the whole of the fix. Same seam, and the same reason, as
 * `accountsMapping`, `journalMapping`, `statementsMapping`, `vatMapping` and
 * `periodsMapping`. This is the sixth such integration.
 *
 * Measured against `/openapi/v1.json` and live responses on **2026-08-02**
 * (8 paths, 10 operations, 24 schemas).
 *
 * ## Systemic divergences
 *
 * | client contract | wire |
 * | --- | --- |
 * | `422` on validation | **`400`**, always — this API has no 422 at all |
 * | money as decimal **strings** (`"1234.56"`) | JSON **numbers** (`cost: 1234.56`) plus the authoritative `costTetri: long` |
 * | route `/fixed-assets/*` | **`api/v1/ledger/fixed-assets/*`** |
 * | `PATCH /{id}` | **`PUT /{id}`** |
 * | `POST /{id}/disposal/{prepare,commit}` | **`POST /{id}/disposal/preview`** and **`POST /{id}/disposal`** |
 * | `POST /depreciation-runs/{preview,commit}` | `…/preview` and **`POST /depreciation-runs`** |
 * | `POST /{id}/acquisition/commit` | **gone** — the server links the entry itself when it posts |
 * | `FixedAssetsQuery.locale` | **removed** — it was accepted and never honoured; nothing sends it now |
 * | `DepreciationRun.id` | **gone** — identity is `period` + `journalEntryId` |
 * | `CommitDepreciationRunResponse.assets[]` | **gone** — committing moves no asset position |
 * | *(absent)* | `GET /reconciliation` — the tie-out, server-computed on both sides |
 * | *(absent)* | `isOpening` / `openingAccumulatedDepreciation` / `openingDepreciatedThrough` |
 * | *(absent)* | `warnings[]` on both disposal responses and on reconciliation |
 * | *(absent)* | `CATCH_UP` run reason; `EntrySource` gained `ACQUISITION` / `DEPRECIATION` / `DISPOSAL` |
 *
 * ## Six properties of the wire this mapping preserves rather than "corrects"
 *
 * 1. **The `*Tetri` integer is authoritative; the decimal companion is an echo.**
 *    The wire ships `cost: 60000` as a JSON *number* next to `costTetri: 6000000`.
 *    Every decimal string downstream is re-derived here from the tetri via
 *    {@link decimalGelOf}, so the two can never disagree on screen. Reading the
 *    float would reintroduce exactly the drift the integer-tetri contract exists
 *    to prevent.
 *
 * 2. **A committed disposal does not dispose the asset.** `CommitDisposalResponse`
 *    returns the asset `ACTIVE`, with `disposalDate`, `disposalProceeds` and
 *    `disposalEntryId` all still null — verified live. This mapping passes that
 *    through untouched. Synthesising a `DISPOSED` status here would make the badge
 *    claim a ledger position that does not exist until the entry posts.
 *
 * 3. **Preview drafts and committed drafts are different shapes, and stay so.**
 *    A preview returns `DraftEntryPreview` (no id, no etag — nothing to open);
 *    a commit returns a full `JournalEntryResponse` (real id, etag, `status: DRAFT`).
 *    {@link mapDraftEntryPreview} and {@link mapJournalEntry} are deliberately
 *    separate, so an unsaved preview cannot reach the journal entry form.
 *
 * 4. **Zero-amount run lines are kept with their reason.** A line the user cannot
 *    explain is indistinguishable from a bug, and `LIFE_ENDED`, `ALREADY_CHARGED`
 *    and `CATCH_UP` mean three different things.
 *
 * 5. **`totalTetri` is the server's, not a sum of the lines.** Carried through
 *    untouched, so a line this mapping had to drop cannot silently change a total.
 *
 * 6. **`warnings[]` are advisory and pass through verbatim.** `message` arrives
 *    localized; `rule` is an open set (`DISPOSAL_LOSS_DEBITS_REVENUE`,
 *    `REGISTER_NE_LEDGER`). Branch on `rule` only where behaviour must differ.
 *
 * ## Measured, and not in the spec
 *
 * - **`locale` is only half-honoured on every draft-building endpoint.** It
 *   localizes account names and the asset name inside the description, but the
 *   verb is always English: `"Acquisition — პრობა"`, `"Depreciation — 2026-07"`.
 *   Same trap as the VAT settlement draft. Default is `ka`; unknown values fall
 *   back to `ka`; the parameter binds case-insensitively.
 * - **`Status` and `Search` bind case-insensitively** (like the journal's
 *   `DateFrom`/`DateTo`), and `Search` matches across *both* locales' names.
 * - **A bad enum or date yields ASP.NET ProblemDetails, not `ApiError`** —
 *   `{type,title,status,errors{},traceId}` with **no `code`**. See
 *   {@link FIXED_ASSET_ERROR_CODES}.
 * - **`source` is silently accepted on `POST /fixed-assets`** (201) rather than
 *   refused with `SOURCE_READONLY` as the journal write edge does. Nothing here
 *   ever sends it regardless — the entries are server-stamped.
 * - **The run line carries no catch-up count.** {@link catchUpInstalmentsOf}
 *   derives it; see the note there.
 */

// ── wire shapes ─────────────────────────────────────────────────────────────

type LocaleMapWire = Record<string, string> | null | undefined;

export type AssetCategoryWire = {
    id?: string;
    code?: string;
    name?: LocaleMapWire;
    costAccountId?: string;
    accumulatedDepreciationAccountId?: string;
    expenseAccountId?: string;
    defaultUsefulLifeMonths?: number;
    archived?: boolean;
};

export type FixedAssetWire = {
    id?: string;
    name?: LocaleMapWire;
    categoryId?: string;
    acquisitionDate?: string;
    cost?: number;
    costTetri?: number;
    salvageValue?: number;
    salvageValueTetri?: number;
    usefulLifeMonths?: number;
    method?: string;
    status?: string;
    isOpening?: boolean;
    disposalDate?: string | null;
    disposalProceeds?: number | null;
    disposalProceedsTetri?: number | null;
    accumulatedDepreciation?: number;
    accumulatedDepreciationTetri?: number;
    netBookValue?: number;
    netBookValueTetri?: number;
    depreciatedThrough?: string | null;
    acquisitionEntryId?: string | null;
    disposalEntryId?: string | null;
    createdAt?: string;
    updatedAt?: string;
};

export type DepreciationScheduleRowWire = {
    period?: string;
    amountTetri?: number;
    accumulatedTetri?: number;
    netBookValueTetri?: number;
    posted?: boolean;
    journalEntryId?: string | null;
};

export type AssetEntryLinkWire = {
    journalEntryId?: string;
    entryNumberFormatted?: string | null;
    kind?: string;
    date?: string;
    period?: string | null;
    amountTetri?: number;
};

export type DepreciationRunLineWire = {
    assetId?: string;
    assetName?: LocaleMapWire;
    categoryId?: string;
    expenseAccountId?: string;
    accumulatedDepreciationAccountId?: string;
    amountTetri?: number;
    reason?: string;
};

export type DepreciationRunWire = {
    period?: string;
    lines?: DepreciationRunLineWire[];
    totalTetri?: number;
    journalEntryId?: string | null;
    committedAt?: string | null;
};

export type DraftEntryPreviewWire = {
    date?: string;
    description?: string;
    lines?: unknown[];
    totalDebitTetri?: number;
    totalCreditTetri?: number;
    validationState?: unknown;
};

export type DisposalPreviewWire = {
    assetId?: string;
    disposalDate?: string;
    proceedsTetri?: number;
    costTetri?: number;
    accumulatedDepreciationTetri?: number;
    netBookValueTetri?: number;
    gainOrLossTetri?: number;
};

/**
 * Same shape as the statements' advisory — reused rather than re-declared, so
 * the two surfaces cannot drift into disagreeing about what a warning is. Only
 * the `rule` vocabulary differs.
 */
type WarningWire = {
    rule?: string;
    message?: string;
    lineId?: string | null;
    accountId?: string | null;
    accountCode?: string | null;
    accountName?: string | null;
    amountTetri?: number | null;
};

export type ReconciliationLineWire = {
    accountId?: string;
    accountCode?: string;
    accountName?: string;
    registerTetri?: number;
    ledgerTetri?: number;
    differenceTetri?: number;
};

// ── primitives ──────────────────────────────────────────────────────────────

const asRecord = (raw: unknown): Record<string, unknown> =>
    typeof raw === 'object' && raw !== null
        ? (raw as Record<string, unknown>)
        : {};

const asArray = (raw: unknown): unknown[] => (Array.isArray(raw) ? raw : []);

/**
 * The wire's `{ en, ka }` map, kept as a map.
 *
 * The register's names are **not** resolved server-side (unlike the statements'
 * line labels or reconciliation's `accountName`), so the display layer picks the
 * locale — and `Search` matches across both, so a Georgian name found by an
 * English query still has to render correctly.
 */
const mapLocaleMap = (
    raw: LocaleMapWire,
): Partial<Record<LocaleCode, string>> => {
    const out: Partial<Record<LocaleCode, string>> = {};
    if (!raw) return out;
    for (const [key, value] of Object.entries(raw)) {
        if (key === 'en' || key === 'ka') out[key] = value;
    }
    return out;
};

/**
 * Only `STRAIGHT_LINE` exists on the wire today. An unknown method maps to it
 * rather than dropping the asset — the register showing a row with the wrong
 * method label is recoverable; the row vanishing is not.
 */
const STRAIGHT_LINE: DepreciationMethod = 'STRAIGHT_LINE';

const mapStatus = (raw: string | undefined): FixedAssetStatus =>
    raw === 'DISPOSED' ? 'DISPOSED' : 'ACTIVE';

const RUN_REASONS: readonly DepreciationRunLineReason[] = [
    'OK',
    'NOT_IN_SERVICE',
    'ALREADY_CHARGED',
    'FULLY_DEPRECIATED',
    'LIFE_ENDED',
    'DISPOSED',
    'CATCH_UP',
];

/**
 * An unrecognised reason becomes `OK` **only when the line actually charges
 * something**; a zero line with an unknown reason stays unexplained rather than
 * being labelled as a normal charge. A new server reason should show up as "no
 * explanation given", not as a silent lie about why a figure is zero.
 */
const mapRunReason = (
    raw: string | undefined,
    amountTetri: number,
): DepreciationRunLineReason => {
    const match = RUN_REASONS.find((reason) => reason === raw);
    if (match) return match;
    return amountTetri === 0 ? 'FULLY_DEPRECIATED' : 'OK';
};

const ENTRY_KINDS: readonly AssetEntryLinkKind[] = [
    'ACQUISITION',
    'DEPRECIATION',
    'DISPOSAL',
];

const mapEntryKind = (raw: string | undefined): AssetEntryLinkKind =>
    ENTRY_KINDS.find((kind) => kind === raw) ?? 'ACQUISITION';

/** `YYYY-MM` → months since year 0, for period arithmetic that never touches Date. */
const periodIndex = (period: string): number | null => {
    const match = /^(\d{4})-(\d{2})$/.exec(period);
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (month < 1 || month > 12) return null;
    return year * 12 + (month - 1);
};

/** `YYYY-MM-DD` → the `YYYY-MM` it falls in. */
const periodOfDate = (date: string): string => date.slice(0, 7);

/**
 * How many monthly instalments a `CATCH_UP` line is charging at once.
 *
 * **The API does not serve this.** The run line carries exactly
 * `{assetId, assetName, categoryId, expenseAccountId,
 * accumulatedDepreciationAccountId, amount, amountTetri, reason}` — measured, no
 * extra fields — yet a catch-up charging several months at once is precisely the
 * case where an unexplained figure reads as a bug. So it is derived from the two
 * things that *are* known: how far the asset has been depreciated, and which
 * period is being run.
 *
 * The count is the number of periods from the first uncharged month through the
 * run period, inclusive. An asset that has never been run counts from its
 * acquisition month. Verified against live data: the Refrigeration unit, charged
 * through `2026-06`, run for `2026-09`, yields 3 — and its ₾1,500 is exactly
 * 3 × its ₾500 instalment.
 *
 * Returns null when the arithmetic cannot be trusted (unparseable periods, or a
 * count that isn't a plural catch-up), because a wrong count is worse than none.
 */
export const catchUpInstalmentsOf = (
    runPeriod: string,
    asset:
        Pick<FixedAsset, 'depreciatedThrough' | 'acquisitionDate'> | undefined,
): number | null => {
    if (!asset) return null;
    const runIndex = periodIndex(runPeriod);
    if (runIndex === null) return null;

    // The first month this run has to cover: the one after the last charged
    // period, or — for an asset that has never been run — its acquisition month.
    // An unparseable `depreciatedThrough` falls through to null rather than to
    // the acquisition month, which would over-count every instalment since.
    let firstUncharged: number | null;
    if (asset.depreciatedThrough) {
        const chargedThrough = periodIndex(asset.depreciatedThrough);
        firstUncharged = chargedThrough === null ? null : chargedThrough + 1;
    } else {
        firstUncharged = periodIndex(periodOfDate(asset.acquisitionDate));
    }
    if (firstUncharged === null) return null;

    const count = runIndex - firstUncharged + 1;
    return count > 1 ? count : null;
};

// ── read edge ───────────────────────────────────────────────────────────────

export const mapAssetCategory = (raw: unknown): AssetCategory => {
    const wire = asRecord(raw) as AssetCategoryWire;
    return {
        id: wire.id ?? '',
        code: wire.code ?? '',
        name: mapLocaleMap(wire.name),
        costAccountId: wire.costAccountId ?? '',
        accumulatedDepreciationAccountId:
            wire.accumulatedDepreciationAccountId ?? '',
        expenseAccountId: wire.expenseAccountId ?? '',
        defaultUsefulLifeMonths: wire.defaultUsefulLifeMonths ?? 0,
        archived: wire.archived ?? false,
    };
};

export const mapAssetCategories = (
    raw: unknown,
): GetAssetCategoriesResponse => ({
    items: asArray(asRecord(raw).items).map(mapAssetCategory),
});

export const mapFixedAsset = (raw: unknown): FixedAsset => {
    const wire = asRecord(raw) as FixedAssetWire;
    const costTetri = wire.costTetri ?? 0;
    const salvageTetri = wire.salvageValueTetri ?? 0;
    const accumulatedTetri = wire.accumulatedDepreciationTetri ?? 0;
    const nbvTetri = wire.netBookValueTetri ?? costTetri - accumulatedTetri;
    const proceedsTetri = wire.disposalProceedsTetri ?? null;

    return {
        id: wire.id ?? '',
        name: mapLocaleMap(wire.name),
        categoryId: wire.categoryId ?? '',
        acquisitionDate: wire.acquisitionDate ?? '',
        cost: decimalGelOf(costTetri),
        costTetri,
        salvageValue: decimalGelOf(salvageTetri),
        salvageValueTetri: salvageTetri,
        usefulLifeMonths: wire.usefulLifeMonths ?? 0,
        method: STRAIGHT_LINE,
        status: mapStatus(wire.status),
        isOpening: wire.isOpening ?? false,
        disposalDate: wire.disposalDate ?? null,
        disposalProceeds:
            proceedsTetri === null ? null : decimalGelOf(proceedsTetri),
        disposalProceedsTetri: proceedsTetri,
        accumulatedDepreciation: decimalGelOf(accumulatedTetri),
        accumulatedDepreciationTetri: accumulatedTetri,
        netBookValue: decimalGelOf(nbvTetri),
        netBookValueTetri: nbvTetri,
        depreciatedThrough: wire.depreciatedThrough ?? null,
        acquisitionEntryId: wire.acquisitionEntryId ?? null,
        disposalEntryId: wire.disposalEntryId ?? null,
        createdAt: wire.createdAt,
        updatedAt: wire.updatedAt,
    };
};

const mapSummary = (raw: unknown): FixedAssetRegisterSummary => {
    const wire = asRecord(raw);
    const totalCostTetri = (wire.totalCostTetri as number) ?? 0;
    const totalAccumTetri =
        (wire.totalAccumulatedDepreciationTetri as number) ?? 0;
    const totalNbvTetri = (wire.totalNetBookValueTetri as number) ?? 0;

    return {
        assetCount: (wire.assetCount as number) ?? 0,
        totalAssetCount: (wire.totalAssetCount as number) ?? 0,
        totalCost: decimalGelOf(totalCostTetri),
        totalCostTetri,
        totalAccumulatedDepreciation: decimalGelOf(totalAccumTetri),
        totalAccumulatedDepreciationTetri: totalAccumTetri,
        totalNetBookValue: decimalGelOf(totalNbvTetri),
        totalNetBookValueTetri: totalNbvTetri,
        costAccountIds: asArray(wire.costAccountIds).filter(
            (id): id is string => typeof id === 'string',
        ),
        accumulatedDepreciationAccountIds: asArray(
            wire.accumulatedDepreciationAccountIds,
        ).filter((id): id is string => typeof id === 'string'),
    };
};

export const mapFixedAssetList = (raw: unknown): GetFixedAssetsResponse => {
    const wire = asRecord(raw);
    return {
        items: asArray(wire.items).map(mapFixedAsset),
        summary: mapSummary(wire.summary),
    };
};

const mapScheduleRow = (raw: unknown): DepreciationScheduleRow => {
    const wire = asRecord(raw) as DepreciationScheduleRowWire;
    const amountTetri = wire.amountTetri ?? 0;
    const accumulatedTetri = wire.accumulatedTetri ?? 0;
    const nbvTetri = wire.netBookValueTetri ?? 0;

    return {
        period: wire.period ?? '',
        amount: decimalGelOf(amountTetri),
        amountTetri,
        accumulated: decimalGelOf(accumulatedTetri),
        accumulatedTetri,
        netBookValue: decimalGelOf(nbvTetri),
        netBookValueTetri: nbvTetri,
        posted: wire.posted ?? false,
        journalEntryId: wire.journalEntryId ?? null,
    };
};

/**
 * A posted entry that moved this asset.
 *
 * **`entries[]` is posted-only** — measured: an outstanding DRAFT disposal does
 * not appear here. The drawer's audit trail is therefore a record of what the
 * ledger holds, never of what is merely intended. An opening asset has an empty
 * trail even though its schedule rows are flagged `posted`, because the opening
 * entry is not linked per-asset.
 */
const mapEntryLink = (raw: unknown): AssetEntryLink => {
    const wire = asRecord(raw) as AssetEntryLinkWire;
    const amountTetri = wire.amountTetri ?? 0;
    return {
        journalEntryId: wire.journalEntryId ?? '',
        entryNumberFormatted: wire.entryNumberFormatted ?? null,
        kind: mapEntryKind(wire.kind),
        date: wire.date ?? '',
        period: wire.period ?? null,
        amount: decimalGelOf(amountTetri),
        amountTetri,
    };
};

export const mapFixedAssetDetail = (raw: unknown): GetFixedAssetResponse => {
    const wire = asRecord(raw);
    return {
        asset: mapFixedAsset(wire.asset),
        schedule: asArray(wire.schedule).map(mapScheduleRow),
        entries: asArray(wire.entries).map(mapEntryLink),
    };
};

// ── drafts and warnings ─────────────────────────────────────────────────────

/** The wire's journal lines, reused so a draft renders through the same table. */
const mapDraftLine = (raw: unknown): JournalLine => {
    const wire = asRecord(raw);
    const debitTetri = (wire.debitTetri as number) ?? 0;
    const creditTetri = (wire.creditTetri as number) ?? 0;
    return {
        id: (wire.id as string) || undefined,
        accountId: (wire.accountId as string) ?? '',
        accountCode: (wire.accountCode as string) ?? '',
        accountName: (wire.accountName as string) ?? '',
        debit: decimalGelOf(debitTetri),
        debitTetri,
        credit: decimalGelOf(creditTetri),
        creditTetri,
        memo: (wire.memo as string) ?? '',
    };
};

/**
 * A preview's unsaved draft. Kept structurally distinct from
 * {@link mapJournalEntry} — see {@link DraftEntryPreview}.
 */
export const mapDraftEntryPreview = (raw: unknown): DraftEntryPreview => {
    const wire = asRecord(raw) as DraftEntryPreviewWire;
    const totalDebitTetri = wire.totalDebitTetri ?? 0;
    const totalCreditTetri = wire.totalCreditTetri ?? 0;
    const lines = asArray(wire.lines).map(mapDraftLine);

    return {
        date: wire.date ?? '',
        description: wire.description ?? '',
        lines,
        totalDebit: decimalGelOf(totalDebitTetri),
        totalDebitTetri,
        totalCredit: decimalGelOf(totalCreditTetri),
        totalCreditTetri,
        // A preview draft carries no `dateInClosedPeriod` — the period gate is
        // the journal form's, applied to the *saved* entry a commit produces.
        // Passing `false` here states only that this preview makes no claim
        // about the period, which is exactly true of an unsaved draft.
        validationState: mapValidationState(
            wire.validationState as Parameters<typeof mapValidationState>[0],
            lines,
            false,
        ),
    };
};

export const mapPlausibilityWarning = (raw: unknown): PlausibilityWarning => {
    const wire = asRecord(raw) as WarningWire;
    return {
        rule: wire.rule ?? '',
        message: wire.message ?? '',
        lineId: wire.lineId ?? null,
        accountId: wire.accountId ?? null,
        accountCode: wire.accountCode ?? null,
        accountName: wire.accountName ?? null,
        amountTetri:
            typeof wire.amountTetri === 'number' ? wire.amountTetri : null,
    };
};

const mapWarnings = (raw: unknown): PlausibilityWarning[] =>
    asArray(raw).map(mapPlausibilityWarning);

/** A loss debited to a Revenue-typed account. Advisory, never blocking. */
export const DISPOSAL_LOSS_DEBITS_REVENUE = 'DISPOSAL_LOSS_DEBITS_REVENUE';

/** The register and the ledger disagree on an account. Advisory; the tie-out states it. */
export const REGISTER_NE_LEDGER = 'REGISTER_NE_LEDGER';

// ── runs ────────────────────────────────────────────────────────────────────

/**
 * A run line, with its catch-up count resolved against the register.
 *
 * `assetsById` is the register the caller already holds; when it is missing the
 * count is simply null rather than guessed.
 */
const mapRunLine = (
    raw: unknown,
    runPeriod: string,
    assetsById: ReadonlyMap<string, FixedAsset> | undefined,
): DepreciationRunLine => {
    const wire = asRecord(raw) as DepreciationRunLineWire;
    const amountTetri = wire.amountTetri ?? 0;
    const reason = mapRunReason(wire.reason, amountTetri);
    const assetId = wire.assetId ?? '';

    return {
        assetId,
        assetName: mapLocaleMap(wire.assetName),
        categoryId: wire.categoryId ?? '',
        expenseAccountId: wire.expenseAccountId ?? '',
        accumulatedDepreciationAccountId:
            wire.accumulatedDepreciationAccountId ?? '',
        amount: decimalGelOf(amountTetri),
        amountTetri,
        reason,
        catchUpInstalments:
            reason === 'CATCH_UP'
                ? catchUpInstalmentsOf(runPeriod, assetsById?.get(assetId))
                : null,
    };
};

export const mapDepreciationRun = (
    raw: unknown,
    assetsById?: ReadonlyMap<string, FixedAsset>,
): DepreciationRun => {
    const wire = asRecord(raw) as DepreciationRunWire;
    const period = wire.period ?? '';
    const totalTetri = wire.totalTetri ?? 0;

    return {
        period,
        lines: asArray(wire.lines).map((line) =>
            mapRunLine(line, period, assetsById),
        ),
        // The server's total, not a sum of the lines — see the header note.
        total: decimalGelOf(totalTetri),
        totalTetri,
        journalEntryId: wire.journalEntryId ?? null,
        committedAt: wire.committedAt ?? null,
    };
};

export const mapPreviewDepreciationRun = (
    raw: unknown,
    assetsById?: ReadonlyMap<string, FixedAsset>,
): PreviewDepreciationRunResponse => {
    const wire = asRecord(raw);
    return {
        run: mapDepreciationRun(wire.run, assetsById),
        draft: wire.draft ? mapDraftEntryPreview(wire.draft) : null,
        existingRun: wire.existingRun
            ? mapDepreciationRun(wire.existingRun, assetsById)
            : null,
    };
};

export const mapCommitDepreciationRun = (
    raw: unknown,
    assetsById?: ReadonlyMap<string, FixedAsset>,
): CommitDepreciationRunResponse => {
    const wire = asRecord(raw);
    return {
        run: mapDepreciationRun(wire.run, assetsById),
        // A commit's draft is a real, saved entry — this is the one the journal
        // form opens. There is no `assets[]`: committing moves nothing.
        draft: mapJournalEntry(wire.draft),
        alreadyExisted: (wire.alreadyExisted as boolean) ?? false,
    };
};

// ── create / update ─────────────────────────────────────────────────────────

export const mapCreateFixedAsset = (raw: unknown): CreateFixedAssetResponse => {
    const wire = asRecord(raw);
    return {
        asset: mapFixedAsset(wire.asset),
        // Null on an opening asset: its cost is already in the ledger through the
        // opening entry, so there is nothing to post.
        draft: wire.draft ? mapJournalEntry(wire.draft) : null,
        alreadyExisted: (wire.alreadyExisted as boolean) ?? false,
    };
};

export const mapUpdateFixedAsset = (
    raw: unknown,
): UpdateFixedAssetResponse => ({
    asset: mapFixedAsset(asRecord(raw).asset),
});

// ── disposal ────────────────────────────────────────────────────────────────

export const mapDisposalPreview = (raw: unknown): DisposalPreview => {
    const wire = asRecord(raw) as DisposalPreviewWire;
    const proceedsTetri = wire.proceedsTetri ?? 0;
    const costTetri = wire.costTetri ?? 0;
    const accumulatedTetri = wire.accumulatedDepreciationTetri ?? 0;
    const nbvTetri = wire.netBookValueTetri ?? 0;
    const gainOrLossTetri = wire.gainOrLossTetri ?? 0;

    return {
        assetId: wire.assetId ?? '',
        disposalDate: wire.disposalDate ?? '',
        proceeds: decimalGelOf(proceedsTetri),
        proceedsTetri,
        cost: decimalGelOf(costTetri),
        costTetri,
        accumulatedDepreciation: decimalGelOf(accumulatedTetri),
        accumulatedDepreciationTetri: accumulatedTetri,
        netBookValue: decimalGelOf(nbvTetri),
        netBookValueTetri: nbvTetri,
        // Signed: negative is a loss. Never take the magnitude — the sign is the
        // whole content of the figure.
        gainOrLoss: decimalGelOf(gainOrLossTetri),
        gainOrLossTetri,
    };
};

export const mapPreviewDisposal = (raw: unknown): PreviewDisposalResponse => {
    const wire = asRecord(raw);
    return {
        preview: mapDisposalPreview(wire.preview),
        draft: mapDraftEntryPreview(wire.draft),
        warnings: mapWarnings(wire.warnings),
    };
};

/**
 * The asset here is still `ACTIVE` and carries no disposal fields — passed
 * through exactly as the server sends it. See property 2 in the header.
 */
export const mapCommitDisposal = (raw: unknown): CommitDisposalResponse => {
    const wire = asRecord(raw);
    return {
        asset: mapFixedAsset(wire.asset),
        draft: mapJournalEntry(wire.draft),
        preview: mapDisposalPreview(wire.preview),
        warnings: mapWarnings(wire.warnings),
        alreadyExisted: (wire.alreadyExisted as boolean) ?? false,
    };
};

// ── reconciliation ──────────────────────────────────────────────────────────

const mapReconciliationLine = (raw: unknown): FixedAssetReconciliationLine => {
    const wire = asRecord(raw) as ReconciliationLineWire;
    const registerTetri = wire.registerTetri ?? 0;
    const ledgerTetri = wire.ledgerTetri ?? 0;
    // The server's difference, not `register − ledger` recomputed here: a second
    // opinion about the one figure the tie-out exists to state is how a screen
    // ends up reporting a difference that isn't real.
    const differenceTetri = wire.differenceTetri ?? 0;

    return {
        accountId: wire.accountId ?? '',
        accountCode: wire.accountCode ?? '',
        accountName: wire.accountName ?? '',
        register: decimalGelOf(registerTetri),
        registerTetri,
        ledger: decimalGelOf(ledgerTetri),
        ledgerTetri,
        difference: decimalGelOf(differenceTetri),
        differenceTetri,
    };
};

export const mapReconciliation = (raw: unknown): FixedAssetReconciliation => {
    const wire = asRecord(raw);
    return {
        tiesOut: (wire.tiesOut as boolean) ?? false,
        cost: asArray(wire.cost).map(mapReconciliationLine),
        accumulatedDepreciation: asArray(wire.accumulatedDepreciation).map(
            mapReconciliationLine,
        ),
        warnings: mapWarnings(wire.warnings),
    };
};

// ── write edge ──────────────────────────────────────────────────────────────

/**
 * Tetri → the JSON **number** the write edge expects.
 *
 * The contract said decimal strings; this API takes doubles. The division is
 * exact for every amount this register can hold (`Number.MAX_SAFE_INTEGER / 100`
 * is ₾90 trillion), and going out through the integer means the value the user
 * typed is never re-parsed from a float. Sub-tetri input is refused server-side
 * with `AMOUNT_PRECISION` rather than rounded, so nothing silently loses a tetri.
 */
/**
 * Re-exported from `@/utils/money` (BOS-162): three mapping files each
 * defined their own copy of this, and callers import it from whichever one
 * they happened to be near.
 */
export { gelNumberOf };

// ── error codes ─────────────────────────────────────────────────────────────

/**
 * Every refusal this surface produces, measured live on 2026-08-02.
 *
 * **The brief named nine; there are at least eighteen.** Each needs its own
 * message — `no raw 400 reaches the user` is an acceptance criterion, and a
 * message that says only "Bad Request" fails it just as surely as a stack trace
 * would.
 *
 * Two envelopes reach the client and only one of them has a `code`:
 *
 * - **`ApiError { error, code }`** — every domain refusal below.
 * - **ASP.NET ProblemDetails `{ type, title, status, errors{}, traceId }`** —
 *   model-binding failures (`Status=BOGUS`, `disposalDate: "nope"`). **No
 *   `code`**, and `message` comes through empty because the text is in `title`.
 *   A handler that only switches on `code` shows a raw 400 for these, which is
 *   why {@link fixedAssetErrorKey} falls back to the field-error envelope before
 *   it falls back to the generic message.
 *
 * `DISPOSAL_RESULT_ACCOUNT_MISSING` is the one code not reproducible on the dev
 * tenant — it needs account 4300 absent — so it is handled from the spec, not
 * from a measurement.
 */
export const FIXED_ASSET_ERROR_CODES = [
    // — the nine the brief named —
    'FUNDING_ACCOUNT_NOT_ALLOWED',
    'OPENING_ACCUMULATED_NEGATIVE',
    'PERIOD_INVALID',
    'NOTHING_TO_DEPRECIATE',
    'DISPOSAL_BEFORE_ACQUISITION',
    'PROCEEDS_NEGATIVE',
    'PROCEEDS_ACCOUNT_REQUIRED',
    'PROCEEDS_ACCOUNT_NOT_ALLOWED',
    'DISPOSAL_RESULT_ACCOUNT_MISSING',
    // — nine more, found by measurement —
    'FUNDING_ACCOUNT_REQUIRED',
    'COST_NOT_POSITIVE',
    'SALVAGE_ABOVE_COST',
    'USEFUL_LIFE_NOT_POSITIVE',
    'CATEGORY_NOT_FOUND',
    'ACCOUNT_NOT_FOUND',
    'AMOUNT_PRECISION',
    'OPENING_POSITION_INCOMPLETE',
    'OPENING_ACCUMULATED_ABOVE_BASE',
] as const;

export type FixedAssetErrorCode = (typeof FIXED_ASSET_ERROR_CODES)[number];

export const isFixedAssetErrorCode = (
    raw: unknown,
): raw is FixedAssetErrorCode =>
    typeof raw === 'string' &&
    (FIXED_ASSET_ERROR_CODES as readonly string[]).includes(raw);
